'use client'

import * as React from 'react'
import { LazyMotion, domAnimation, m, useInView, useReducedMotion } from 'framer-motion'
import { Trophy, Vote } from 'lucide-react'
import { EASE } from '@/lib/motion'

// The hero leaderboard for /ugc-competitions, now a motion leaf: rows
// settle in top-to-bottom and each score bar fills (scaleX, origin-left)
// the first time the card scrolls into view. Plays once; reduced motion
// renders the final board immediately. All values are illustrative demo
// data for the mock contest card, same as the rest of the preview chrome.

const ROWS = [
  { rank: 1, handle: '@sky.b',       score: 8.4, votes: 1842 },
  { rank: 2, handle: '@maya.k',      score: 8.2, votes: 1721 },
  { rank: 3, handle: '@noor.studio', score: 7.9, votes: 1503 },
  { rank: 4, handle: '@otto.frame',  score: 7.6, votes: 1402 },
  { rank: 5, handle: '@river.amel',  score: 7.4, votes: 1287 },
  { rank: 6, handle: '@lila.maker',  score: 7.1, votes: 1199 },
]

const MAX_SCORE = 10

function rankTone(rank: number) {
  if (rank === 1) return 'bg-yellow-100 text-yellow-700'
  if (rank === 2) return 'bg-slate-200 text-slate-700'
  if (rank === 3) return 'bg-orange-100 text-orange-700'
  return 'bg-secondary text-muted-foreground'
}

export function CompetitionLeaderboard() {
  const ref = React.useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' })
  const reduce = useReducedMotion()
  const done = inView
  const d = (n: number) => (reduce ? 0 : n)

  return (
    <LazyMotion features={domAnimation}>
      <div ref={ref} className="overflow-hidden rounded-3xl border bg-card shadow-2xl shadow-primary/10">
        <div className="flex items-center justify-between border-b bg-secondary/40 px-5 py-3">
          <div className="flex items-center gap-2">
            <Trophy className="h-4 w-4 text-primary" />
            <span className="text-[11px] font-medium uppercase tracking-wider text-foreground">#LumenSummer · day 7 of 14</span>
          </div>
          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-emerald-700 dark:text-emerald-400">Live</span>
        </div>
        <ul className="divide-y">
          {ROWS.map((r, i) => (
            <m.li
              key={r.rank}
              className="flex items-center gap-3 px-5 py-3"
              initial={reduce ? false : { opacity: 0, y: 10 }}
              animate={done ? { opacity: 1, y: 0 } : undefined}
              transition={{ duration: 0.4, ease: EASE, delay: d(0.1 + i * 0.07) }}
            >
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-medium ${rankTone(r.rank)}`}>
                {r.rank}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-mono text-sm">{r.handle}</span>
                <span className="mt-1 block h-1 w-full max-w-[160px] overflow-hidden rounded-full bg-secondary">
                  <m.span
                    className="block h-full rounded-full bg-primary/70"
                    style={{ originX: 0, width: `${(r.score / MAX_SCORE) * 100}%` }}
                    initial={reduce ? false : { scaleX: 0 }}
                    animate={done ? { scaleX: 1 } : undefined}
                    transition={{ duration: 0.5, ease: EASE, delay: d(0.25 + i * 0.07) }}
                  />
                </span>
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">{r.votes.toLocaleString()} votes</span>
              <span className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary tabular-nums">{r.score.toFixed(1)}</span>
            </m.li>
          ))}
        </ul>
        <div className="flex items-center justify-between border-t px-5 py-3 text-[11px] text-muted-foreground">
          <span>4,212 entries · 38,109 votes</span>
          <span className="flex items-center gap-1.5"><Vote className="h-3 w-3" /> Vote rate-limited</span>
        </div>
      </div>
    </LazyMotion>
  )
}
